import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Lightning, SidebarSimple, SignOut, type Icon } from "@phosphor-icons/react";
import { DropdownMenu } from "radix-ui";
import { AnimatePresence, motion } from "motion/react";
import { Link, useLocation } from "wouter";
import { cn } from "./cn";
import { Count } from "./Count";
import { ownsKeys } from "./Kbd";
import { Logo } from "./Logo";
import { Mark, MARK } from "./Mark";
import { Monogram } from "./Monogram";
import { spring } from "./motion";
import { Tooltip, TooltipProvider } from "./Tooltip";
import { useDesktop } from "./useMedia";

export type NavItem = {
  href: string;
  label: string;
  icon: Icon;
};

export type ShellProps = {
  nav: NavItem[];
  /** `name` is what the avatar's initials come from. */
  user: { email: string; name: string };
  onSignOut: () => void;
  /** What waits on the user. While `count` is above zero a pill in the mobile header calls `open`. */
  needs?: { count: number; open: () => void };
  /** Where the Upgrade entry goes, above the account row on desktop and in the mobile header. Only set for free users. */
  upgrade?: string;
  children: ReactNode;
};

const RAIL_KEY = "headcount.sidebar";
/** How far the edge has to be dragged before the sidebar snaps to the other width. */
const SNAP = 32;
const SHORTCUT = typeof navigator !== "undefined" && /Mac|iP(hone|ad)/.test(navigator.userAgent) ? "⌘B" : "Ctrl B";

function storedRail(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === "rail";
  } catch {
    return false;
  }
}

/** Whether the desktop sidebar is folded to its icon rail: remembered, and toggled with ⌘B or Ctrl+B outside text fields, dialogs and menus. */
function useRail() {
  const [rail, setRail] = useState(storedRail);
  const desktop = useDesktop();

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_KEY, rail ? "rail" : "full");
    } catch {}
  }, [rail]);

  useEffect(() => {
    if (!desktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "b" || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.repeat || ownsKeys(e.target)) return;
      e.preventDefault();
      setRail((r) => !r);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [desktop]);

  return [rail, setRail] as const;
}

function renderIcon(Glyph: Icon) {
  return <Glyph weight="duotone" aria-hidden />;
}

function isActive(location: string, href: string) {
  if (href === "/") return location === "/";
  return location === href || location.startsWith(href + "/");
}

/**
 * The signed-in frame. Desktop: a fixed white sidebar with the logo, the nav and the account row, beside a white
 * content column; it folds to an icon rail from its toggle, ⌘B, or by dragging its edge. Mobile: a slim top bar with
 * the logo and a bottom tab bar. The sidebar's current width is `--sidebar-w` for anything inside.
 */
export function Shell({ nav, user, onSignOut, needs, upgrade, children }: ShellProps) {
  const [location] = useLocation();
  const [rail, setRail] = useRail();
  const waiting = needs && needs.count > 0 ? needs : null;
  const toggle = (
    <Tooltip label={rail ? "Expand sidebar" : "Collapse sidebar"} keys={SHORTCUT}>
      <motion.button
        type="button"
        onClick={() => setRail(!rail)}
        aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!rail}
        whileTap={{ scale: 0.9 }}
        transition={spring}
        className="grid size-8 shrink-0 place-items-center rounded-control text-muted transition-[background-color,color] duration-150 hover:bg-sunk hover:text-ink"
      >
        <SidebarSimple size={18} weight="duotone" aria-hidden />
      </motion.button>
    </Tooltip>
  );

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-page text-ink" style={{ "--sidebar-w": rail ? "var(--spacing-rail)" : "var(--spacing-sidebar)" } as CSSProperties}>
        <a
          href="#main"
          className="sr-only z-[70] rounded-control bg-primary px-3.5 py-2 text-sm font-semibold text-on-primary focus-visible:not-sr-only focus-visible:fixed focus-visible:left-3 focus-visible:top-3"
        >
          Skip to content
        </a>
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-(--sidebar-w) border-r border-line bg-surface transition-[width] duration-200 ease-out-soft md:block">
          <div className="flex h-full flex-col overflow-x-clip">
            <div
              className={cn(
                "flex h-16 shrink-0 items-center justify-between pr-3 transition-[padding] duration-200 ease-out-soft",
                rail ? "pl-[26px]" : "pl-5",
              )}
            >
              <Link href="/" className="-mx-1.5 rounded-control px-1.5 py-1" aria-label="Headcount home">
                {rail ? <Mark size={24} style={{ marginInline: -Math.round((24 * MARK.inset) / 40) }} /> : <Logo size={22} />}
              </Link>
              {rail ? null : toggle}
            </div>
            <nav aria-label="Main" className="flex flex-col gap-0.5 px-3 pt-2">
              {nav.map((item) => {
                const active = isActive(location, item.href);
                return (
                  <Tooltip key={item.href} label={item.label} hidden={!rail}>
                    <Link
                      href={item.href}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "relative flex h-10 items-center gap-3 rounded-control px-[11px] text-sm font-semibold transition-[background-color,color] duration-150",
                        "[&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:transition-[color] [&_svg]:duration-150",
                        active ? "text-primary-ink [&_svg]:text-primary" : "text-ink-soft hover:bg-sunk hover:text-ink [&_svg]:text-muted hover:[&_svg]:text-ink",
                      )}
                    >
                      {active ? <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-control bg-primary-soft" transition={spring} /> : null}
                      <span className="relative inline-flex">
                        {renderIcon(item.icon)}
                      </span>
                      <span className={cn("relative min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-150", rail && "opacity-0")}>{item.label}</span>
                    </Link>
                  </Tooltip>
                );
              })}
            </nav>
            <div className="mt-auto">
              {upgrade ? (
                <div className="px-3 pb-2">
                  <Tooltip label="Upgrade" hidden={!rail}>
                    <Link
                      href={upgrade}
                      aria-current={isActive(location, upgrade) ? "page" : undefined}
                      className={cn(
                        "flex h-10 items-center gap-3 rounded-control px-[11px] text-sm font-semibold text-primary-ink transition-[background-color] duration-150 hover:bg-primary-soft aria-[current=page]:bg-primary-soft",
                        "[&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:text-accent",
                      )}
                    >
                      <Lightning weight="fill" aria-hidden />
                      <span className={cn("min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-opacity duration-150", rail && "opacity-0")}>Upgrade</span>
                    </Link>
                  </Tooltip>
                </div>
              ) : null}
              {rail ? <div className="px-4 pb-2">{toggle}</div> : null}
              <div className="border-t border-line p-3">
                <div className="flex items-center gap-2.5 rounded-control py-1 pl-1 pr-1">
                  {rail ? (
                    <Account user={user} onSignOut={onSignOut} />
                  ) : (
                    <>
                      <Monogram name={user.name} logo={false} size="sm" className="rounded-full" />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink-soft" title={user.email}>
                        {user.email}
                      </span>
                      <Tooltip label="Sign out" side="top">
                        <motion.button
                          type="button"
                          onClick={onSignOut}
                          aria-label="Sign out"
                          whileTap={{ scale: 0.9 }}
                          transition={spring}
                          className="grid size-8 shrink-0 place-items-center rounded-control text-muted transition-[background-color,color] duration-150 hover:bg-sunk hover:text-ink"
                        >
                          <SignOut size={16} weight="bold" aria-hidden />
                        </motion.button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <EdgeRail rail={rail} onRail={setRail} />
        </aside>

        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-line bg-surface/90 px-4 pt-safe backdrop-blur-xl md:hidden">
          <Link href="/" aria-label="Headcount home" className="rounded-control">
            <Logo size={19} />
          </Link>
          <div className="flex items-center gap-2.5">
            <AnimatePresence initial={false}>
              {waiting ? (
                <motion.button
                  key="needs"
                  type="button"
                  onClick={waiting.open}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileTap={{ scale: 0.95 }}
                  transition={spring}
                  className="flex h-8 items-center gap-2 whitespace-nowrap rounded-control bg-accent-soft pl-2.5 pr-1.5 text-[13px] font-semibold text-tone-accent-ink"
                >
                  Needs you
                  <Count value={waiting.count} tone="brand" />
                </motion.button>
              ) : null}
            </AnimatePresence>
            {upgrade ? (
              <Link href={upgrade} className="flex h-8 items-center gap-1 whitespace-nowrap rounded-control px-1.5 text-[13px] font-semibold text-primary-ink [&_svg]:text-accent">
                <Lightning size={15} weight="fill" aria-hidden />
                Upgrade
              </Link>
            ) : null}
            <Monogram name={user.name} logo={false} size="sm" className="rounded-full" />
          </div>
        </header>

        <main
          id="main"
          className="min-h-dvh px-4 pb-[calc(env(safe-area-inset-bottom)+6rem)] pt-5 md:pb-16 md:pl-[calc(var(--sidebar-w)+2.5rem)] md:pr-10 md:pt-10 md:transition-[padding-left] md:duration-200 md:ease-out-soft"
        >
          <div className="mx-auto w-full max-w-[1180px]">{children}</div>
        </main>

        <nav
          aria-label="Main"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] backdrop-blur-xl md:hidden"
        >
          <ul className="flex px-2">
            {nav.map((item) => {
              const active = isActive(location, item.href);
              return (
                <li key={item.href} className="flex-1">
                  <Link
                    href={item.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex min-h-16 flex-col items-center justify-center gap-1 text-[11.5px] font-semibold transition-[color] duration-150",
                      "[&_svg]:size-[21px] [&_svg]:transition-[color] [&_svg]:duration-150",
                      active ? "text-primary-ink [&_svg]:text-primary" : "text-muted",
                    )}
                  >
                    <motion.span className="relative grid h-8 w-14 place-items-center" whileTap={{ scale: 0.9 }} transition={spring}>
                      {active ? <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-control bg-primary-soft" transition={spring} /> : null}
                      <span className="relative inline-flex">{renderIcon(item.icon)}</span>
                    </motion.span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </TooltipProvider>
  );
}

/** The folded account row: the avatar, which opens a small menu with the email and sign out. */
function Account({ user, onSignOut }: { user: ShellProps["user"]; onSignOut: () => void }) {
  return (
    <DropdownMenu.Root modal={false}>
      <Tooltip label={user.email}>
        <DropdownMenu.Trigger asChild>
          <motion.button type="button" aria-label="Account" whileTap={{ scale: 0.92 }} transition={spring} className="shrink-0 rounded-full">
            <Monogram name={user.name} logo={false} size="sm" className="rounded-full" />
          </motion.button>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content side="right" align="end" sideOffset={12} collisionPadding={12} className="z-50 outline-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, x: -4 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={spring}
            style={{ transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)" }}
            className="w-56 rounded-card bg-surface p-1 shadow-raised"
          >
            <DropdownMenu.Label className="truncate px-2.5 pb-1.5 pt-2 text-[12.5px] font-semibold text-muted">{user.email}</DropdownMenu.Label>
            <DropdownMenu.Item
              onSelect={onSignOut}
              className="flex h-9 cursor-pointer select-none items-center rounded-control px-2.5 text-[13.5px] font-semibold text-ink outline-none transition-[background-color] duration-100 data-[highlighted]:bg-sunk"
            >
              Sign out
            </DropdownMenu.Item>
          </motion.div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** The sidebar's right edge: dragging it past `SNAP` folds or unfolds the sidebar, and a click toggles it. */
function EdgeRail({ rail, onRail }: { rail: boolean; onRail: (rail: boolean) => void }) {
  const drag = useRef<{ x: number; rail: boolean; moved: boolean } | null>(null);
  const moved = useRef(false);
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, rail, moved: false };
        moved.current = false;
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        if (Math.abs(dx) > 3) d.moved = moved.current = true;
        if (d.rail ? dx > SNAP : dx < -SNAP) {
          d.rail = !d.rail;
          d.x = e.clientX;
          onRail(d.rail);
        }
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onClick={() => {
        if (!moved.current) onRail(!rail);
      }}
      className={cn(
        "absolute inset-y-0 -right-2 z-10 w-4 cursor-ew-resize touch-none select-none outline-none",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:bg-primary after:opacity-0 after:transition-opacity after:duration-150 hover:after:opacity-100 active:after:opacity-100",
      )}
    />
  );
}
